import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftRight, Baby } from 'lucide-react';
import {
  switchToChild, switchToParent, useHasPin, useManagedBy, useMyChildren,
} from '@core/firebase/children';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/cn';

/**
 * Handing the device to a child, and taking it back.
 *
 * Both directions replace the session on this device, because a child has a
 * uid of their own and everything they recite must be recorded against them.
 * That is also why the hand-over is REFUSED until the parent has a PIN: going
 * the other way needs one, and a parent without one would be stranded in
 * their child's account with only a verification code — of which they get
 * five a day — to get out.
 */
export function ProfileSwitcher({ item, onDone }: { item: string; onDone: () => void }) {
  const { identity } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const uid = identity?.userId ?? null;
  const { managedBy } = useManagedBy(uid);
  const { children: everyone } = useMyChildren(uid);
  /*
   * Only accounts this reader OWNS. Handing over mints a session as that
   * pupil — becoming them — which a guardian who merely redeemed an invite
   * (م-3-01) may not do. The server refuses it either way; a control whose
   * only outcome is that refusal should not be on the screen.
   */
  const children = everyone.filter((c) => c.relation === 'owner');
  const { hasPin } = useHasPin(uid);

  const [asking, setAsking] = useState(false);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const back = async () => {
    if (busy) return;
    setBusy(true);
    setErrorKey(null);
    try {
      await switchToParent(pin);
      navigate('/home', { replace: true });
      setPin('');
      setAsking(false);
      onDone();
    } catch (e) {
      const message = String((e as { message?: string })?.message ?? '');
      setErrorKey(
        message.includes('too-many-attempts') ? 'children.pinLocked'
          : message.includes('wrong-pin') ? 'children.pinWrong'
            : 'children.switchFailed'
      );
    } finally {
      setBusy(false);
    }
  };

  const hand = async (childUid: string) => {
    if (busy) return;
    setBusy(true);
    setErrorKey(null);
    try {
      await switchToChild(childUid);
      // Move the READER too, not only the session. Left where they were, a
      // child lands on the parent's own أبنائي page — seen live — which then
      // offers her a guardian PIN of her own.
      navigate('/home', { replace: true });
      onDone();
    } catch {
      setErrorKey('children.switchFailed');
    } finally {
      setBusy(false);
    }
  };

  // A child's session: the only move is back, and it costs the PIN.
  if (managedBy) {
    return (
      <div data-testid="switcher-child-mode" className="mt-1.5 border-t border-hair pt-1.5">
        <p className="px-3 pb-1 pt-2 text-xs font-medium text-helper">{t('children.childMode')}</p>
        {!asking ? (
          <button
            type="button"
            data-testid="back-to-parent"
            onClick={() => setAsking(true)}
            className={cn(item, 'text-ink hover:bg-surface')}
          >
            <ArrowLeftRight className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-start">{t('children.backToMe')}</span>
          </button>
        ) : (
          <div className="space-y-2 px-3 py-2">
            <p className="text-xs text-helper">{t('children.pinPrompt')}</p>
            <input
              data-testid="pin-input"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder={t('children.pinPlaceholder')}
              inputMode="numeric"
              // A PIN is a secret even on the device it is typed on.
              type="password"
              dir="ltr"
              className="h-11 w-full rounded-[14px] border border-hair bg-surface text-center tracking-[0.5em] text-ink outline-none focus:border-primary"
            />
            {errorKey && (
              <p data-testid="pin-error" role="alert" className="text-sm text-danger">{t(errorKey)}</p>
            )}
            <button
              type="button"
              data-testid="confirm-pin"
              onClick={() => { void back(); }}
              disabled={busy}
              className="h-11 w-full rounded-full bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-60"
            >
              {t('children.backToMe')}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (children.length === 0) return null;

  return (
    <div data-testid="switcher-parent-mode" className="mt-1.5 border-t border-hair pt-1.5">
      <p className="px-3 pb-1 pt-2 text-xs font-medium text-helper">{t('children.handOver')}</p>
      {/*
        * Refused without a PIN rather than offered and then failing: the
        * failure would land AFTER the session had already changed hands.
        */}
      {!hasPin ? (
        <Link
          to="/children"
          data-testid="need-pin"
          onClick={onDone}
          className={cn(item, 'text-primary hover:bg-surface')}
        >
          <Baby className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-start">{t('children.needPinFirst')}</span>
        </Link>
      ) : (
        children.map((child) => (
          <button
            key={child.uid}
            type="button"
            data-testid={`hand-over-${child.uid}`}
            onClick={() => { void hand(child.uid); }}
            disabled={busy}
            className={cn(item, 'text-ink hover:bg-surface disabled:opacity-60')}
          >
            <Baby className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate text-start">{child.displayName}</span>
          </button>
        ))
      )}
      {errorKey && (
        <p data-testid="switch-error" role="alert" className="px-3 pb-2 text-sm text-danger">
          {t(errorKey)}
        </p>
      )}
    </div>
  );
}
